import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "fcv_cart_v1";

const initialState = {
  items: [], // [{ colorId, colorName, colorCode, swatch, quantity }]
  shipping: "fr",
  promoCode: null, // { code, percent_off, label }
};

function loadState() {
  if (typeof window === "undefined") return initialState;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw);
    return {
      items: Array.isArray(parsed.items) ? parsed.items : [],
      shipping: parsed.shipping || "fr",
      promoCode: parsed.promoCode || null,
    };
  } catch {
    return initialState;
  }
}

export function CartProvider({ children }) {
  const [state, setState] = useState(loadState);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }, [state]);

  const value = useMemo(() => {
    const totalQuantity = state.items.reduce((s, i) => s + i.quantity, 0);

    const addItem = (color, quantity = 1) => {
      setState((prev) => {
        const existing = prev.items.find((i) => i.colorId === color.id);
        const items = existing
          ? prev.items.map((i) =>
              i.colorId === color.id ? { ...i, quantity: Math.min(50, i.quantity + quantity) } : i
            )
          : [
              ...prev.items,
              {
                colorId: color.id,
                colorName: color.name,
                colorCode: color.code,
                swatch: color.swatch,
                quantity,
              },
            ];
        return { ...prev, items };
      });
      setIsOpen(true);
    };

    const updateQty = (colorId, quantity) => {
      setState((prev) => ({
        ...prev,
        items: prev.items
          .map((i) => (i.colorId === colorId ? { ...i, quantity } : i))
          .filter((i) => i.quantity > 0),
      }));
    };

    const removeItem = (colorId) => {
      setState((prev) => ({ ...prev, items: prev.items.filter((i) => i.colorId !== colorId) }));
    };

    const clear = () => setState(initialState);

    const setShipping = (shipping) => setState((prev) => ({ ...prev, shipping }));

    const applyPromo = (promoCode) => setState((prev) => ({ ...prev, promoCode }));
    const clearPromo = () => setState((prev) => ({ ...prev, promoCode: null }));

    return {
      items: state.items,
      shipping: state.shipping,
      promoCode: state.promoCode,
      totalQuantity,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      addItem,
      updateQty,
      removeItem,
      clear,
      setShipping,
      applyPromo,
      clearPromo,
    };
  }, [state, isOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
